import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FaPlus, FaTrash, FaLock, FaFileExcel, FaBoxOpen, FaTools, FaEdit, FaSave, FaTimes, FaPaperclip } from "react-icons/fa";
import api from "../../../libs/axios";
import { useAuth } from "../../../provider/auth.provider";
import { useConfirm } from "../../../provider/confirm.provider";
import IncFilesPanel from "../components/IncFilesPanel";
import { SelectWithSearch, SelectOption } from "../../../utils/select-with-search";
import { ExcelDownload } from "../../../utils/excel.utils";
import UnitSelect from "../components/UnitSelect";

type ItemType = "material" | "service";
// The per-BOQ sub-tabs: its two line types, plus the documents attached to it.
type BoqSubTab = ItemType | "documents";

const BOQ_DOCUMENT_TYPES = [
  { value: "boq", label: "BOQ Copy" },
  { value: "client_signoff", label: "Client Sign-off" },
  { value: "wcc", label: "WCC" },
  { value: "other", label: "Other" },
];

interface LineForm {
  item: SelectOption | null;
  service_name: string;
  desc: string;
  unit: string;
  qty: string;
  rate: string;
  billable: boolean;
  remarks: string;
}

const EMPTY_LINE: LineForm = {
  item: null,
  service_name: "",
  desc: "",
  unit: "",
  qty: "",
  rate: "",
  billable: true,
  remarks: "",
};

const num = (v: any) => Number(v || 0);
const money = (v: any) => num(v).toFixed(2);

// A row belongs to the Material tab unless it is explicitly a service. Rows
// written before item_type existed only carry the legacy is_service_charge flag.
const typeOf = (it: any): ItemType =>
  (it?.item_type || (it?.is_service_charge ? "service" : "material")) as ItemType;

// `locked` comes from inc_sites.is_locked — set by "Finalize Billing". A locked
// site is frozen and the API answers 423 on every write, so the write controls
// are folded into the permission flags rather than left on screen to fail.
const BoqTab: React.FC<{ siteId: string; locked?: boolean }> = ({ siteId, locked = false }) => {
  const { hasPermission } = useAuth();
  // Opening the tab and creating a BOQ. Viewing and exporting survive the
  // lock; every write below does not.
  const canManage = hasPermission("IncSite", "boq");
  const canCreate = canManage && !locked;
  // Editing the BOQ header and its material/service lines.
  const canEdit = hasPermission("IncSite", "boq_edit") && !locked;
  // Finalizing locks the BOQ and is what billing is raised against, so it
  // is gated separately from editing lines.
  const canFinalize = hasPermission("IncSite", "boq_finalize") && !locked;
  const canExport = hasPermission("IncSite", "export_boq");
  // Uploading / deleting BOQ documents. Viewing them only needs the tab. A
  // finalized BOQ still takes documents (the signed copy arrives afterwards);
  // only a locked site does not.
  const canManageDocs = hasPermission("IncSite", "boq_files") && !locked;
  const { confirm } = useConfirm();
  const [boqs, setBoqs] = useState<any[]>([]);
  const [docCounts, setDocCounts] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [boqType, setBoqType] = useState("site_wise");
  const [newBoqBillable, setNewBoqBillable] = useState(true);
  const [saving, setSaving] = useState(false);

  const [activeTab, setActiveTab] = useState<Record<number, BoqSubTab>>({});
  const [lineFor, setLineFor] = useState<Record<number, LineForm>>({});

  const [showExport, setShowExport] = useState(false);

  // Inline edit of one BOQ line, keyed by item id. Only one row is open at a
  // time so "save" is never ambiguous.
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [editItemForm, setEditItemForm] = useState({
    service_name: "",
    desc: "",
    unit: "",
    qty: "",
    rate: "",
    billable: true,
    remarks: "",
  });
  const [savingItem, setSavingItem] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/inc/sites/${siteId}/boqs`);
      const rows = res.data?.data || [];
      setBoqs(rows);
      setDocCounts(Object.fromEntries(rows.map((b: any) => [b.id, Number(b.document_count || 0)])));
    } catch {
      toast.error("Failed to load BOQs");
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    load();
  }, [load]);

  const createBoq = async () => {
    setSaving(true);
    try {
      await api.post(`/inc/sites/${siteId}/boqs`, { boq_type: boqType, is_billable: newBoqBillable });
      toast.success("BOQ created");
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to create BOQ");
    } finally {
      setSaving(false);
    }
  };

  const finalize = async (id: number) => {
    const ok = await confirm({
      title: `Finalize BOQ #${id}?`,
      message: "A finalized BOQ is locked — its lines can no longer be added, edited or removed. Billing is raised against it.",
      confirmText: "Finalize BOQ",
      variant: "lock",
    });
    if (!ok) return;
    try {
      await api.post(`/inc/boqs/${id}/finalize`, {});
      toast.success("BOQ finalized");
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to finalize BOQ");
    }
  };

  const toggleBoqBillable = async (boq: any) => {
    try {
      await api.put(`/inc/boqs/${boq.id}`, { is_billable: !(boq.is_billable !== false) });
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to update BOQ");
    }
  };

  const removeItem = async (itemId: number) => {
    const ok = await confirm({
      title: "Remove this line?",
      message: "The line is deleted from the BOQ.",
      confirmText: "Remove",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/inc/boq-items/${itemId}`);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to remove item");
    }
  };

  const startEditItem = (it: any) => {
    setEditingItem(it.id);
    setEditItemForm({
      service_name: it.service_name || "",
      desc: it.description || "",
      unit: it.unit || "",
      qty: String(it.quantity ?? ""),
      rate: String(it.rate ?? ""),
      billable: it.billable !== false,
      remarks: it.remarks || "",
    });
  };

  const saveEditItem = async (it: any) => {
    const type = typeOf(it);
    if (type === "service" && !editItemForm.service_name.trim()) {
      return toast.error("Enter a service name");
    }
    setSavingItem(true);
    try {
      // item_type and the material identity are not editable inline — a line
      // that needs to change identity is removed and re-added, which keeps the
      // material/service split honest.
      await api.put(`/inc/boq-items/${it.id}`, {
        service_name: type === "service" ? editItemForm.service_name.trim() : null,
        description: editItemForm.desc,
        unit: editItemForm.unit,
        quantity: num(editItemForm.qty),
        rate: num(editItemForm.rate),
        billable: editItemForm.billable,
        remarks: editItemForm.remarks,
      });
      setEditingItem(null);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to update line");
    } finally {
      setSavingItem(false);
    }
  };

  const addLine = async (boqId: number, type: ItemType) => {
    const st = lineFor[boqId] || EMPTY_LINE;
    if (type === "material" && !st.item) return toast.error("Select a material item");
    if (type === "service" && !st.service_name.trim()) return toast.error("Enter a service name");
    try {
      await api.post(`/inc/boqs/${boqId}/items`, {
        item_type: type,
        material_item_id: type === "material" ? st.item?.id : null,
        service_name: type === "service" ? st.service_name.trim() : null,
        description: st.desc,
        unit: st.unit,
        quantity: num(st.qty),
        rate: num(st.rate),
        billable: st.billable,
        remarks: st.remarks,
      });
      setLineFor((p) => ({ ...p, [boqId]: { ...EMPTY_LINE } }));
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to add line");
    }
  };

  // Flat export of every line across every BOQ, with the BOQ header fields
  // attached to each row so the sheet stands on its own.
  const exportRows = useMemo(
    () =>
      boqs.flatMap((boq: any) =>
        (boq.items || []).map((it: any) => ({
          "BOQ ID": boq.id,
          "BOQ Type": boq.boq_type || "",
          "BOQ Status": boq.status || "",
          "BOQ Billable": boq.is_billable !== false ? "Yes" : "No",
          "Line Type": typeOf(it) === "service" ? "Service" : "Material",
          "Item Code": it.material_item?.item_code || "",
          "Item Name": it.material_item?.item_name || "",
          Service: it.service_name || "",
          Description: it.description || "",
          Unit: it.unit || "",
          Quantity: num(it.quantity),
          Rate: num(it.rate),
          Amount: num(it.amount),
          Billable: it.billable ? "Yes" : "No",
          Remarks: it.remarks || "",
        })),
      ),
    [boqs],
  );

  const handleExport = () => {
    if (!exportRows.length) return toast.error("No BOQ lines to export");
    setShowExport(true);
  };

  const inp = "px-2 py-2 text-sm border border-gray-300 rounded";

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;

  return (
    <div>
      {locked && (
        <p className="text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded px-3 py-2 mb-3">
          Billing has been finalized for this site — it is locked and this tab is read-only.
        </p>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        {canCreate ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">BOQ type</label>
              <select value={boqType} onChange={(e) => setBoqType(e.target.value)} className={inp}>
                <option value="site_wise">Site Wise</option>
                <option value="period_wise">Period Wise</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input
                type="checkbox"
                checked={newBoqBillable}
                onChange={(e) => setNewBoqBillable(e.target.checked)}
                className="w-4 h-4 text-blue-800 border-gray-300 rounded"
              />
              BOQ is billable
            </label>
            <button
              onClick={createBoq}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-800 rounded-md hover:bg-blue-900 disabled:bg-blue-400"
            >
              <FaPlus className="w-3 h-3" /> New BOQ
            </button>
          </div>
        ) : (
          <span />
        )}
        {canExport && (
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700"
          >
            <FaFileExcel className="w-4 h-4" /> Export Excel
          </button>
        )}
      </div>

      {boqs.length === 0 ? (
        <p className="text-sm text-gray-500">No BOQs.</p>
      ) : (
        <div className="space-y-4">
          {boqs.map((boq) => {
            const st = lineFor[boq.id] || EMPTY_LINE;
            const subTab: BoqSubTab = activeTab[boq.id] || "material";
            const showDocuments = subTab === "documents";
            // Line tables below only ever render for the two line types.
            const tab: ItemType = showDocuments ? "material" : subTab;
            const allItems: any[] = boq.items || [];
            const materials = allItems.filter((it) => typeOf(it) === "material");
            const services = allItems.filter((it) => typeOf(it) === "service");
            const rows = tab === "material" ? materials : services;
            const tabTotal = rows.reduce((s: number, it: any) => s + num(it.amount), 0);
            const grandTotal = allItems.reduce((s: number, it: any) => s + num(it.amount), 0);
            const locked = boq.status === "finalized";
            const boqBillable = boq.is_billable !== false;
            const editable = canEdit && !locked;

            return (
              <div key={boq.id} className="border border-gray-200 rounded-lg p-3">
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="text-sm font-medium text-gray-800 flex flex-wrap items-center gap-2">
                    <span>BOQ #{boq.id}</span>
                    <span className="text-xs text-gray-500">({boq.boq_type})</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${locked ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                      {boq.status}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${boqBillable ? "bg-blue-100 text-blue-800" : "bg-gray-200 text-gray-700"}`}>
                      {boqBillable ? "Billable" : "Non-Billable"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {editable && (
                      <label className="flex items-center gap-2 text-xs text-gray-700">
                        <input
                          type="checkbox"
                          checked={boqBillable}
                          onChange={() => toggleBoqBillable(boq)}
                          className="w-4 h-4 text-blue-800 border-gray-300 rounded"
                        />
                        BOQ is billable
                      </label>
                    )}
                    {canFinalize && !locked && (
                      <button
                        onClick={() => finalize(boq.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-indigo-700 border border-indigo-200 bg-indigo-50 rounded hover:bg-indigo-100"
                      >
                        <FaLock className="w-3 h-3" /> Finalize
                      </button>
                    )}
                  </div>
                </div>

                {/* Material / Service / Documents tabs */}
                <div className="flex gap-1 border-b border-gray-200 mb-3">
                  {([
                    { key: "material" as BoqSubTab, label: "Material", icon: FaBoxOpen, count: materials.length },
                    { key: "service" as BoqSubTab, label: "Service", icon: FaTools, count: services.length },
                    { key: "documents" as BoqSubTab, label: "Documents", icon: FaPaperclip, count: docCounts[boq.id] || 0 },
                  ]).map(({ key, label, icon: Icon, count }) => (
                    <button
                      key={key}
                      onClick={() => setActiveTab((p) => ({ ...p, [boq.id]: key }))}
                      className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        subTab === key
                          ? "border-blue-800 text-blue-800"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {label}
                      <span className={`px-1.5 py-0.5 text-xs rounded-full ${subTab === key ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-600"}`}>
                        {count}
                      </span>
                    </button>
                  ))}
                </div>

                {showDocuments ? (
                  <div className="mb-3">
                    <IncFilesPanel
                      filesUrl={`/inc/boqs/${boq.id}/files`}
                      deleteUrl={(fileId) => `/inc/boq-files/${fileId}`}
                      folderName="inc/boq-documents"
                      canManage={canManageDocs}
                      title={`BOQ #${boq.id} documents`}
                      emptyLabel="No documents attached to this BOQ yet."
                      documentTypes={BOQ_DOCUMENT_TYPES}
                      onCountChange={(count) => setDocCounts((p) => ({ ...p, [boq.id]: count }))}
                    />
                  </div>
                ) : (
                <>
                {/* Lines */}
                {rows.length === 0 ? (
                  <p className="text-sm text-gray-500 mb-3">No {tab} lines.</p>
                ) : (
                  <div className="overflow-x-auto mb-3">
                    <table className="min-w-full text-sm">
                      <thead className="text-xs uppercase text-gray-500 bg-gray-50">
                        <tr>
                          {tab === "material" ? (
                            <>
                              <th className="text-left px-2 py-1.5">Item Code</th>
                              <th className="text-left px-2 py-1.5">Item Name</th>
                            </>
                          ) : (
                            <th className="text-left px-2 py-1.5">Service</th>
                          )}
                          <th className="text-left px-2 py-1.5">Description</th>
                          <th className="text-left px-2 py-1.5">Unit</th>
                          <th className="text-right px-2 py-1.5">Qty</th>
                          <th className="text-right px-2 py-1.5">Rate</th>
                          <th className="text-right px-2 py-1.5">Amount</th>
                          <th className="text-center px-2 py-1.5">Billable</th>
                          <th className="text-left px-2 py-1.5">Remarks</th>
                          {editable && <th className="text-right px-2 py-1.5">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((it: any) =>
                          editingItem === it.id ? (
                            <tr key={it.id} className="border-t border-gray-100 align-top bg-blue-50/40">
                              {tab === "material" ? (
                                <>
                                  <td className="px-2 py-1.5 whitespace-nowrap">{it.material_item?.item_code || "-"}</td>
                                  <td className="px-2 py-1.5">{it.material_item?.item_name || "-"}</td>
                                </>
                              ) : (
                                <td className="px-2 py-1.5">
                                  <input
                                    value={editItemForm.service_name}
                                    onChange={(e) => setEditItemForm({ ...editItemForm, service_name: e.target.value })}
                                    className={`${inp} w-40`}
                                  />
                                </td>
                              )}
                              <td className="px-2 py-1.5">
                                <input
                                  value={editItemForm.desc}
                                  onChange={(e) => setEditItemForm({ ...editItemForm, desc: e.target.value })}
                                  className={`${inp} w-40`}
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <UnitSelect
                                  value={editItemForm.unit}
                                  onChange={(unit) => setEditItemForm({ ...editItemForm, unit })}
                                  className="w-28"
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  value={editItemForm.qty}
                                  onChange={(e) => setEditItemForm({ ...editItemForm, qty: e.target.value })}
                                  className={`${inp} w-20 text-right`}
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  value={editItemForm.rate}
                                  onChange={(e) => setEditItemForm({ ...editItemForm, rate: e.target.value })}
                                  className={`${inp} w-24 text-right`}
                                />
                              </td>
                              <td className="px-2 py-1.5 text-right text-gray-600">
                                {money(num(editItemForm.qty) * num(editItemForm.rate))}
                              </td>
                              <td className="px-2 py-1.5 text-center">
                                <input
                                  type="checkbox"
                                  checked={editItemForm.billable}
                                  onChange={(e) => setEditItemForm({ ...editItemForm, billable: e.target.checked })}
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  value={editItemForm.remarks}
                                  onChange={(e) => setEditItemForm({ ...editItemForm, remarks: e.target.value })}
                                  className={`${inp} w-32`}
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <div className="flex justify-end gap-1">
                                  <button
                                    onClick={() => saveEditItem(it)}
                                    disabled={savingItem}
                                    title="Save"
                                    className="p-1.5 text-green-700 hover:bg-green-50 rounded disabled:opacity-60"
                                  >
                                    <FaSave className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => setEditingItem(null)}
                                    title="Cancel"
                                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                                  >
                                    <FaTimes className="w-3 h-3" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            <tr key={it.id} className="border-t border-gray-100 align-top">
                              {tab === "material" ? (
                                <>
                                  <td className="px-2 py-1.5 whitespace-nowrap">{it.material_item?.item_code || "-"}</td>
                                  <td className="px-2 py-1.5">{it.material_item?.item_name || "-"}</td>
                                </>
                              ) : (
                                <td className="px-2 py-1.5">{it.service_name || "-"}</td>
                              )}
                              <td className="px-2 py-1.5 text-gray-600 break-words max-w-xs">{it.description || "-"}</td>
                              <td className="px-2 py-1.5">{it.unit || "-"}</td>
                              <td className="px-2 py-1.5 text-right">{it.quantity}</td>
                              <td className="px-2 py-1.5 text-right">{it.rate}</td>
                              <td className="px-2 py-1.5 text-right">{it.amount}</td>
                              <td className="px-2 py-1.5 text-center">
                                <span className={`px-2 py-0.5 text-xs rounded-full ${it.billable ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"}`}>
                                  {it.billable ? "Yes" : "No"}
                                </span>
                              </td>
                              <td className="px-2 py-1.5 text-gray-600 break-words max-w-xs">{it.remarks || "-"}</td>
                              {editable && (
                                <td className="px-2 py-1.5">
                                  <div className="flex justify-end gap-1">
                                    <button onClick={() => startEditItem(it)} title="Edit" className="p-1.5 text-blue-700 hover:bg-blue-50 rounded">
                                      <FaEdit className="w-3 h-3" />
                                    </button>
                                    <button onClick={() => removeItem(it.id)} title="Remove" className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                                      <FaTrash className="w-3 h-3" />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ),
                        )}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-gray-200 font-medium">
                          <td className="px-2 py-1.5" colSpan={tab === "material" ? 6 : 5}>
                            {tab === "material" ? "Material" : "Service"} Total
                          </td>
                          <td className="px-2 py-1.5 text-right">{tabTotal.toFixed(2)}</td>
                          <td colSpan={editable ? 3 : 2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                {/* Add line — same fields for both tabs, only the identity differs */}
                {editable && (
                  <div className="flex flex-wrap gap-2 items-end bg-gray-50 border border-gray-200 rounded p-2">
                    {tab === "material" ? (
                      <div className="min-w-[200px]">
                        <label className="block text-xs text-gray-500 mb-1">Item</label>
                        <SelectWithSearch
                          value={st.item}
                          onChange={(o) => setLineFor((p) => ({ ...p, [boq.id]: { ...st, item: o } }))}
                          apiEndpoint="/inc/material-items"
                          dataKey="items"
                          labelKey="item_name"
                          displayFormat={(i) => `${i.item_code} — ${i.item_name}`}
                          placeholder="Select item"
                          minSearchLength={0}
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Service</label>
                        <input
                          placeholder="Service name"
                          value={st.service_name}
                          onChange={(e) => setLineFor((p) => ({ ...p, [boq.id]: { ...st, service_name: e.target.value } }))}
                          className={inp}
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Description</label>
                      <input
                        placeholder="Description"
                        value={st.desc}
                        onChange={(e) => setLineFor((p) => ({ ...p, [boq.id]: { ...st, desc: e.target.value } }))}
                        className={inp}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Unit</label>
                      <UnitSelect
                        value={st.unit}
                        onChange={(unit) => setLineFor((p) => ({ ...p, [boq.id]: { ...st, unit } }))}
                        className="w-32"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Qty</label>
                      <input
                        placeholder="Qty"
                        value={st.qty}
                        onChange={(e) => setLineFor((p) => ({ ...p, [boq.id]: { ...st, qty: e.target.value } }))}
                        className={`${inp} w-20`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Rate</label>
                      <input
                        placeholder="Rate"
                        value={st.rate}
                        onChange={(e) => setLineFor((p) => ({ ...p, [boq.id]: { ...st, rate: e.target.value } }))}
                        className={`${inp} w-24`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Amount</label>
                      <input value={money(num(st.qty) * num(st.rate))} readOnly className={`${inp} w-28 bg-gray-100 text-gray-600`} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Remarks</label>
                      <input
                        placeholder="Remarks"
                        value={st.remarks}
                        onChange={(e) => setLineFor((p) => ({ ...p, [boq.id]: { ...st, remarks: e.target.value } }))}
                        className={inp}
                      />
                    </div>
                    <label className="flex items-center gap-1 text-xs pb-2.5">
                      <input
                        type="checkbox"
                        checked={st.billable}
                        onChange={(e) => setLineFor((p) => ({ ...p, [boq.id]: { ...st, billable: e.target.checked } }))}
                      />
                      Billable
                    </label>
                    <button
                      onClick={() => addLine(boq.id, tab)}
                      className="px-3 py-2 text-sm text-white bg-blue-800 rounded hover:bg-blue-900"
                    >
                      Add {tab === "material" ? "Material" : "Service"}
                    </button>
                  </div>
                )}
                </>
                )}

                <div className="mt-3 pt-2 border-t border-gray-200 text-sm text-gray-700 text-right">
                  <span className="text-gray-500 mr-2">BOQ Total (Material + Service)</span>
                  <span className="font-semibold">{grandTotal.toFixed(2)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ExcelDownload
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        data={exportRows}
        filename={`inc_boq_site_${siteId}`}
        title="Export BOQ"
        description="Download BOQ material and service lines"
      />
    </div>
  );
};

export default BoqTab;
